/**
 * Guided, step-by-step variant selection — with NO page reload between products.
 *
 * The subscription family spans several Shopify products. Two of the steps are really *which
 * product* the customer lands on, not variant options:
 *   - HEIGHT (crew / quarter / ankle / no-show) -> a separate product
 *   - PACK SIZE (3-pack / 6-pack ...)           -> a separate product
 * ...while COLOR and SIZE are ordinary options inside whichever product is loaded. Order is
 * HEIGHT -> COLOR -> SIZE -> PACK SIZE -> (optional) FREQUENCY.
 *
 * HEIGHT and PACK resolve to a product handle via the family map (see snippets/guided-selector
 * .liquid, `data-guided-family`). Picking HEIGHT loads that height's product so COLOR/SIZE can
 * render (defaulting the pack underneath, but leaving the PACK step unchosen). Picking PACK last
 * swaps to the sibling product, carrying COLOR/SIZE/selling_plan forward. Swaps fetch() the sibling
 * product's full page and replace the whole <product-form> in place — no navigation (see
 * swapToUrl()'s own doc comment for why this fetches the full page rather than just its section).
 *
 * Single source of truth per step is the DOM: on any change we re-read every step, so every step
 * is gated by one uniform rule (a step unlocks once every step before it has a value). Emits
 * `on:guided:change` for the sticky "Your Sox Box" bar to render from.
 *
 * Required translation strings: productsProductGuidedStepProgress, productsProductGuidedStepComplete
 */

if (!customElements.get('guided-selector')) {
  class GuidedSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.progress = this.querySelector('[data-guided-progress]');
      this.progressTrack = this.querySelector('.guided-progress__track');
      this.progressLabel = this.querySelector('[data-guided-progress-label]');
      this.priceMap = this.readPriceMap();

      // The HEIGHT x PACK -> product map, plus which height/pack this loaded product is.
      this.family = this.readFamily();
      this.buildMap();
      this.buildPackList();
      this.renderPackButtons();

      this.steps = Array.from(this.querySelectorAll('.guided-step')).map((el) => ({
        el,
        kind: el.matches('[data-guided-height-step]')
          ? 'height'
          : el.matches('[data-guided-pack-step]')
            ? 'pack'
            : el.matches('[data-guided-frequency-step]')
              ? 'frequency'
              : 'option'
      }));

      // Only real user input should scroll a newly unlocked step into view. VariantPicker's
      // applySearchParams() fires synthetic change events on load; those must stay silent.
      this.userInteracted = false;
      this.addEventListener('change', (evt) => { if (evt.isTrusted) this.userInteracted = true; }, true);

      // Only a genuine user change advances the accordion. The variant picker's applySearchParams()
      // restores carried options with SYNTHETIC change events (isTrusted === false) after a swap —
      // advancing on those makes the open step bounce around (e.g. flashing back to Size).
      this.addEventListener('change', (evt) => {
        const read = this.refresh();
        if (evt.isTrusted) {
          // The quiz owns its step navigation. Running the standard accordion scroll here
          // causes a second, competing desktop scroll whenever a quiz choice changes.
          if (this.dataset.guidedView !== 'subscription2-0-quiz') this.advanceAccordion(read);
          this.hideAddedMessage();
        }
      });
      this.addEventListener('on:variant:change', () => this.refresh());

      // Reset the flow after a successful add-to-cart so the customer can build another box.
      // on:cart:add is dispatched by <product-form> (an ancestor), so it's only reachable via
      // document — see assets/main.js ProductForm.handleSubmit(). The message itself lives under
      // the Add to Cart button (see sections/main-product.liquid's buy-buttons block), a sibling
      // rather than a descendant, so it's found by id (data-added-msg-id, set from Liquid — both ids
      // derive from the same product_form_id, so they always pair up).
      this.addedMsg = this.dataset.addedMsgId ? document.getElementById(this.dataset.addedMsgId) : null;
      if (!this.addedMsg) console.warn('guided-selector: added-msg not found for id', this.dataset.addedMsgId);
      const addAnotherBtn = this.addedMsg && this.addedMsg.querySelector('[data-guided-add-another]');
      if (!addAnotherBtn && this.addedMsg) console.warn('guided-selector: [data-guided-add-another] not found in', this.addedMsg);
      if (addAnotherBtn) {
        addAnotherBtn.addEventListener('click', (evt) => {
        evt.preventDefault();

        this.hideAddedMessage();
        this.resetFlow();

       window.scrollTo({top: 0,behavior: 'smooth'});
       });
      }
      this.boundHandleCartAdd = this.handleCartAdd.bind(this);
      document.addEventListener('on:cart:add', this.boundHandleCartAdd);

      this.heightButtons = Array.from(this.querySelectorAll('.guided-height'));
      this.heightButtons.forEach((btn) => {
        btn.addEventListener('click', this.handleHeightClick.bind(this));
        btn.addEventListener('mouseenter', this.handleHeightHover.bind(this));
      });
      // No hover-prefetch for Pack (unlike Height's mouseenter above) — intentionally: Pack is
      // reached only after Height/Color/Size, so by the time these tiles are visible the customer is
      // deep in the flow and the round-trip savings matter less; also, pack tiles are regenerated by
      // renderPackButtons() on every height change, so hover-prefetch wiring would need to be
      // reattached there too. Not worth the complexity for this step specifically.
      this.querySelectorAll('.guided-pack').forEach((btn) => {
        btn.addEventListener('click', this.handlePackClick.bind(this));
      });

      // Turn every step into an accordion row (one open at a time), then open the first
      // unanswered step.
      this.setupAccordion();

      // The native subscription/frequency card (top of the selector), if present.
      this.freqCard = this.querySelector('[data-guided-frequency-card]');
      this.setupFrequencyCard();

      // After a swap, restore the height/pack/frequency chosen before it (variant options are
      // restored by the variant picker's own applySearchParams()).
      this.applyDimsFromUrl();
      this.applyFrequencyFromUrl();
      this.updatePackAvailability(this.getSelectedHeight());
      this.refresh();

      // Open the correct step AFTER the variant picker (which upgrades after this element) has
      // restored carried options in the same task. Advancing synchronously here would land on Size
      // before it's restored and flash it open after a pack swap.
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.advanceAccordion(this.refresh());
      });
    }

    disconnectedCallback() {
      if (this.boundHandleCartAdd) document.removeEventListener('on:cart:add', this.boundHandleCartAdd);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    /** @returns {object} Map of "{variantId}:{planId}" -> { price, compareAt } formatted strings. */
    readPriceMap() {
      const el = this.querySelector('[data-guided-prices]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        console.error('guided-selector: bad price map', err);
        return {};
      }
    }

    /** @returns {{ current: object, products: Array }} The family map handed over by Liquid. */
    readFamily() {
      const el = this.querySelector('[data-guided-family]');
      if (!el) return { current: {}, products: [] };
      try {
        const data = JSON.parse(el.textContent);
        return { current: data.current || {}, products: data.products || [] };
      } catch (err) {
        console.error('guided-selector: bad family map', err);
        return { current: {}, products: [] };
      }
    }

    /** Builds { height: { pack: productUrl } } from the family products. */
    buildMap() {
      this.map = {};
      this.family.products.forEach((pr) => {
        if (!pr.height || !pr.pack) return;
        if (!this.map[pr.height]) this.map[pr.height] = {};
        this.map[pr.height][pr.pack] = pr.url;
      });
    }

    /** Distinct pack sizes across the whole family, ascending, with their labels. */
    buildPackList() {
      const seen = new Map();
      this.family.products.forEach((pr) => {
        if (pr.pack && !seen.has(pr.pack)) seen.set(pr.pack, pr.packLabel || pr.pack);
      });
      this.packList = Array.from(seen.entries())
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => (parseInt(a.key, 10) || 0) - (parseInt(b.key, 10) || 0));
    }

    renderPackButtons() {
      const wrap = this.querySelector('[data-guided-pack-btns]');
      if (!wrap || !this.packList.length) return;
      const showStartingPrice = ['subscription2-0-quiz', 'subscription-2-0'].includes(this.dataset.guidedView);
      // Tile shows the count big with "Pack" beneath (e.g. "6 / PACK"). In the quiz,
      // the price is filled once Height is known by updatePackAvailability().
      wrap.innerHTML = this.packList.map((p) => `
        <button type="button" class="opt-label opt-label--tile btn relative text-center guided-pack"
                data-guided-pack="${this.escape(p.key)}" aria-pressed="false">
          <span class="opt-label--tile__num">${this.escape(p.key)}</span>
          <span class="opt-label--tile__unit js-value">${this.escape(p.packUnit || 'Pack')}</span>
          <span class="guided-pack__price"${showStartingPrice ? '' : ' hidden'}></span>
        </button>`).join('');
    }

    /**
     * Reads one step's satisfied state, chosen value and human label.
     * @param {object} step - { el, kind }.
     * @returns {{ satisfied: boolean, value: string, label: string, key: string }}
     */
    readStep(step) {
      const { el, kind } = step;

      if (kind === 'height') {
        const sel = el.querySelector('.guided-height.is-selected .guided-product__title');
        return {
          key: 'height',
          label: this.labelOf(el),
          value: sel ? sel.textContent.trim() : '',
          satisfied: !!sel
        };
      }

      if (kind === 'pack') {
        const btn = el.querySelector('.guided-pack.is-selected');
        // Look up the label from packList (built once in buildPackList()) rather than re-parsing the
        // rendered tile's text nodes — same data, one source of truth.
        const entry = btn ? this.packList.find((p) => p.key === btn.dataset.guidedPack) : null;
        const value = entry ? entry.label : (btn ? btn.textContent.trim() : '');
        return { key: 'pack', label: this.labelOf(el), value: value, satisfied: !!btn };
      }

      if (kind === 'frequency') {
        const checked = el.querySelector('input[name="selling_plan"]:checked');
        return {
          key: 'frequency',
          label: this.labelOf(el),
          value: checked ? (checked.dataset.planName || '') : '',
          satisfied: !!checked
        };
      }

      // Variant option — dropdown or listed radios.
      let value = null;
      if (el.dataset.selectorType === 'dropdown') {
        const opt = el.querySelector('.custom-select__option[aria-selected="true"]');
        value = opt && opt.dataset.value !== '' ? opt.dataset.value : null;
      } else {
        const checked = el.querySelector('input:checked');
        value = checked ? checked.value : null;
      }
      return {
        key: el.dataset.option || 'option',
        label: this.labelOf(el),
        value: value || '',
        satisfied: value !== null
      };
    }

    labelOf(el) {
      const label = el.querySelector('.label, legend.label');
      return label ? label.textContent.trim() : '';
    }

    /** Locks/unlocks steps, updates the progress bar, price, ATC state and emits on:guided:change. */
    refresh() {
      const read = this.steps.map((step) => this.readStep(step));
      const freq = this.readFrequency();

      // Accordion gating: a step unlocks once every step before it has a value.
      let completedSteps = 0;
      for (let i = 0; i < read.length; i += 1) {
        if (read[i].satisfied) completedSteps += 1;
        else break;
      }
      this.steps.forEach((step, index) => {
        const isUnlocked = index <= completedSteps;
        step.el.classList.toggle('is-unlocked', isUnlocked);
        step.el.classList.toggle('is-complete', read[index].satisfied);
        step.el.toggleAttribute('inert', !isUnlocked);
        step.el.setAttribute('aria-disabled', String(!isUnlocked));
      });

      // MARK:- Treat Recharge cadence as the final required accordion step.
      // Frequency is the final accordion step, so the step list is also the progress list.
      const segments = read.slice();

      // Progress / current segment = the run of satisfied segments from the start.
      let barCompleted = 0;
      for (let i = 0; i < segments.length; i += 1) {
        if (segments[i].satisfied) barCompleted += 1;
        else break;
      }

      const complete = read.every((r) => r.satisfied);
      this.updateProgress(barCompleted, segments.length);
      this.updatePriceAndCart(freq, complete);
      this.emitChange(segments, barCompleted, complete);
      return read;
    }

    /**
     * Reads the native subscription/one-time + frequency card (top of the selector), if present.
     * @returns {?{key,label,value,satisfied,planId,planName}} null when there's no frequency card.
     */
    readFrequency() {
      if (!this.freqCard) return null;
      // No one-time option exists anymore (guided-frequency-card.liquid renders exactly one, always-
      // checked [data-freq-type], value="subscription") — always reading the selling plan is correct.
      const plan = this.freqCard.querySelector('input[name="selling_plan"]:checked');
      return {
        key: 'frequency',
        label: 'Frequency',
        value: plan ? (plan.dataset.planName || '') : '',
        satisfied: !!plan,
        planId: plan ? plan.value : null,
        planName: plan ? (plan.dataset.planName || '') : ''
      };
    }

    /**
     * Updates the price for the chosen variant + selling plan, and drives the guided ATC / reveal state.
     * @param {?object} freq - The frequency readout from readFrequency(), or null.
     * @param {boolean} complete - Are all steps (incl. frequency) satisfied?
     */
    updatePriceAndCart(freq, complete) {
      const variant = this.variantPicker ? this.variantPicker.variant : null;
      const planId = freq && freq.planId ? freq.planId : null;

      // On the quiz frequency step, show the current variant's actual price for every cadence.
      // The price map is generated from Shopify selling-plan allocations for the current product.
      if (this.freqCard && ['subscription2-0-quiz', 'subscription-2-0'].includes(this.dataset.guidedView)) {
        this.freqCard.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          const label = input.nextElementSibling;
          const priceEl = label ? label.querySelector('[data-guided-plan-price]') : null;
          if (!priceEl) return;

          const entry = variant ? this.priceMap[`${variant.id}:${input.value}`] : null;
          priceEl.innerHTML = entry && entry.price ? entry.price : '';
          priceEl.hidden = !(entry && entry.price);
        });
      }

      // Subscription (plan) price beats base price when both are known.
      let price = null;
      let compareAt = null;
      if (variant && planId && this.priceMap[`${variant.id}:${planId}`]) {
        const entry = this.priceMap[`${variant.id}:${planId}`];
        price = entry.price;
        compareAt = entry.compareAt || null;
      } else if (variant && this.variantPicker.data && this.variantPicker.data.formatted[variant.id]) {
        price = this.variantPicker.data.formatted[variant.id].price;
      }

      // Reflect the plan price on the main price block and the card's subscription price.
      if (price) {
        const current = this.mainPriceEl && this.mainPriceEl.querySelector('.price__current');
        if (current) current.innerHTML = price;
        if (planId && this.freqCard) {
          const subPrice = this.freqCard.querySelector('[data-freq-sub-price]');
          if (subPrice) subPrice.innerHTML = price;
        }
      }

      this.state = {
        variant,
        variantId: variant ? String(variant.id) : null,
        available: !!(variant && variant.available),
        sellingPlan: planId ? { id: planId, name: freq.planName || '' } : null,
        price,
        compareAt,
        complete
      };

      // Reveal deferred blocks (e.g. a lingering app widget) and gate the main ATC. Only a complete
      // AND in-stock selection can be added — a sold-out combo stays disabled.
      const canAdd = complete && this.state.available;

      // Sold-out message: only when the customer has finished choosing but the combo is unavailable.
      const soldOutMsg = this.querySelector('[data-guided-soldout]');
      if (soldOutMsg) soldOutMsg.hidden = !(complete && !this.state.available);
      const section = this.closest('.js-product');
      if (section) {
        // Only for is-guided-complete, which CSS reads scoped to .js-product specifically
        // (.js-product.is-guided-complete .guided-deferred — see guided-selector.css) to reveal a
        // deferred app block that lives inside <product-form>. Don't reuse this for anything that
        // also needs to reach <sticky-atc> (a sibling of <product-form>, not a descendant) — see
        // formsSection below.
        section.classList.toggle('is-guided-complete', complete);
      }

      // The sticky Add to Cart bar (snippets/sticky-atc.liquid) renders its own independent
      // <sticky-atc> form as a SIBLING of <product-form>, not a descendant — .section-main-product
      // (the section's own wrapper class) is the nearest ancestor shared by both, so gating scoped
      // here reaches both forms/buttons uniformly instead of only the main one.
      const formsSection = this.closest('.section-main-product');
      if (formsSection) {
        formsSection.querySelectorAll('.js-product-form [name="add"]').forEach((addBtn) => {
          if (canAdd) {
            addBtn.disabled = false;
            addBtn.removeAttribute('data-guided-disabled');
          } else {
            addBtn.disabled = true;
            addBtn.setAttribute('data-guided-disabled', '');
          }
        });

        // Belt-and-braces: the theme's variant picker (and the Recharge widget) can re-enable a
        // button once a valid variant is picked — before pack/frequency are chosen. Block the actual
        // add in the capture phase until the whole flow is complete and in stock. Both the main form
        // and sticky-atc's form get their own guard (each with its own dedup flag).
        // NOTE: capture: true here does NOT guarantee this runs before ProductForm.handleSubmit
        // (main.js) on the SAME <form> — the capture flag only orders listeners across different
        // ancestor phases; two listeners on one target still fire in registration order, and
        // ProductForm's own submit listener registers earlier (during the element's own constructor,
        // before this element's connectedCallback/refresh() ever runs). The disabled attribute set
        // just above is the actual defense — a real browser won't submit a disabled button at all,
        // with or without listeners. This guard only helps for the moment before a re-render.
        formsSection.querySelectorAll('.js-product-form').forEach((form) => {
          if (form.dataset.guidedGuard) return;
          form.dataset.guidedGuard = 'true';
          form.addEventListener('submit', (evt) => {
            if (!this.state || !this.state.complete || !this.state.available) {
              evt.preventDefault();
              evt.stopImmediatePropagation();
              this.advanceAccordion();
            }
          }, true);
        });
      }
    }

    get mainPriceEl() {
      const section = this.closest('.js-product');
      return section ? section.querySelector('.product-info__price .price') : null;
    }

    /** Emits the state the sticky "Your Sox Box" bar renders from. */
    emitChange(segments, completed, complete) {
      this.dispatchEvent(new CustomEvent('on:guided:change', {
        bubbles: true,
        detail: {
          segments: segments.map((r) => ({ key: r.key, label: r.label, value: r.value })),
          currentStep: complete ? segments.length : completed,
          totalSteps: segments.length,
          complete,
          ...this.state
        }
      }));
    }

    updateProgress(completed, totalSegments) {
      if (!this.progress) return;
      const total = totalSegments || this.steps.length;
      const current = Math.min(completed, total);
      const isComplete = current === total;

      this.progressTrack.style.setProperty('--guided-progress', `${(current / total) * 100}%`);
      this.progressTrack.setAttribute('aria-valuenow', String(current));
      this.progress.classList.toggle('is-complete', isComplete);

      const label = isComplete
        ? theme.strings.productsProductGuidedStepComplete
        : theme.strings.productsProductGuidedStepProgress
          .replace('[CURRENT]', current)
          .replace('[TOTAL]', total);

      this.progressTrack.setAttribute('aria-valuetext', label);
      if (this.progressLabel) this.progressLabel.textContent = label;
    }

    // --- Add to cart / reset ---------------------------------------------------------------------

    /** Only react to an add-to-cart from OUR OWN section — not some other product form on the page.
     * Checks shared .section-main-product ancestry rather than exact-element equality with the main
     * <product-form>, since a successful add via the sticky ATC bar (snippets/sticky-atc.liquid,
     * a sibling <sticky-atc> form, not a descendant of <product-form>) should also reset the flow and
     * show the added message — it's the same section's cart-add either way. */
    handleCartAdd(evt) {
     const mySection = this.closest('.section-main-product');
     const gateFails = !mySection || !evt.target || evt.target.closest('.section-main-product') !== mySection;
     console.warn('guided-selector: handleCartAdd fired', { gateFails, evtTarget: evt.target, mySection });

     if (gateFails) return;

     if (this.addedMsg) {
     this.addedMsg.hidden = false;
    }
} 

    /** Clears every step (and the frequency card back to its default) so the customer can build
     * another box. Mirrors the fresh-page-load state: nothing selected, first step open. */
    resetFlow() {
      const heightPackBefore = this.querySelectorAll('.guided-height.is-selected, .guided-pack.is-selected').length;
      const checkedBefore = this.querySelectorAll('.option-selector:not(.option-selector--custom) input:checked').length;
      console.warn('guided-selector: resetFlow() start', { heightPackBefore, checkedBefore });

      this.querySelectorAll('.guided-height.is-selected, .guided-pack.is-selected').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      this.updatePackAvailability(null);

      this.querySelectorAll('.option-selector:not(.option-selector--custom) input:checked').forEach((r) => {
        r.checked = false;
      });
      this.querySelectorAll('.js-color-text, .js-guided-height-text, .js-guided-pack-text').forEach((el) => { el.textContent = ''; });
      // A plain property change doesn't notify <variant-picker> — it only recomputes on 'change'.
      if (this.variantPicker) this.variantPicker.dispatchEvent(new CustomEvent('change', { bubbles: true }));

      // Clearing the DOM isn't enough on its own: the URL still carries every choice from the
      // subscription that was just added (guided_height, guided_pack, each option like Color/Size,
      // selling_plan, variant). Nothing else strips these, and the NEXT swap (clicking Height is the
      // natural next step) reads window.location.search to seed its freshly-constructed
      // <variant-picker>/<guided-selector> — silently repopulating the OLD subscription's choices,
      // which is what actually reads as "it didn't reset." Strip everything this flow carries except
      // `view`, which is what keeps the guided template active at all on family members' own product
      // pages — losing it here would break the very next swap. Option keys come from `this.steps`
      // (the same source getCarryOverParams() uses), so there's one definition of "which keys are
      // ours," not a second hardcoded list to keep in sync.
      const url = new URL(window.location.href);
      ['guided_height', 'guided_pack', 'selling_plan', 'variant'].forEach((k) => url.searchParams.delete(k));
      this.steps
        .filter((step) => step.kind === 'option')
        .forEach((step) => url.searchParams.delete(step.el.dataset.option));
      window.history.replaceState({}, '', url.href);

      const heightPackAfter = this.querySelectorAll('.guided-height.is-selected, .guided-pack.is-selected').length;
      const checkedAfter = this.querySelectorAll('.option-selector:not(.option-selector--custom) input:checked').length;
      console.warn('guided-selector: resetFlow() mid (after clearing, before refresh/openStep)', { heightPackAfter, checkedAfter });

      if (this.freqCard) {
        const sub = this.freqCard.querySelector('[data-freq-type][value="subscription"]');
        if (sub) sub.checked = true;
        const plans = Array.from(this.freqCard.querySelectorAll('input[name="selling_plan"]'));
        plans.forEach((p) => { p.checked = false; });
        this.syncFrequencyCard();
      }

      this.refresh();
      this.openStep(this.steps[0] ? this.steps[0].el : null);
      console.warn('guided-selector: resetFlow() end', {
        openStepIsFirst: this.steps[0] && this.steps[0].el.classList.contains('is-open'),
        heightPackFinal: this.querySelectorAll('.guided-height.is-selected, .guided-pack.is-selected').length,
        checkedFinal: this.querySelectorAll('.option-selector:not(.option-selector--custom) input:checked').length
      });
    }

    hideAddedMessage() {
      if (this.addedMsg && !this.addedMsg.hidden) this.addedMsg.hidden = true;
    }

    // --- Accordion ------------------------------------------------------------------------------

    /**
     * Turns each step's label into a collapsible header (chevron + click/keyboard toggle), so only
     * one step is open at a time. Option steps get a "Select " prefix to read like "SELECT COLOR".
     */
    setupAccordion() {
      this.classList.add('is-accordion');
      this.steps.forEach((step) => {
        const label = step.el.querySelector('.label, legend.label');
        if (!label || label.dataset.guidedHeader) return;
        label.dataset.guidedHeader = 'true';

        // Read the option-step label like "SELECT COLOR" to match the height/pack/frequency labels.
        if (step.kind === 'option') {
          const first = label.firstChild;
          const text = first && first.nodeType === Node.TEXT_NODE ? first.textContent : '';
          if (!/^\s*select\b/i.test(text)) {
            label.insertBefore(document.createTextNode('Select '), label.firstChild);
          }
        }

        // Use the whole row as the clickable header (so the chevron sits far right). When the label
        // shares a flex row with a size-guide link, toggle from that row but let the link work.
        const row = label.parentElement && label.parentElement.classList.contains('flex')
          ? label.parentElement
          : label;

        const chevron = document.createElement('span');
        chevron.className = 'guided-step__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        row.appendChild(chevron);

        row.classList.add('guided-step__header');
        row.setAttribute('role', 'button');
        row.setAttribute('tabindex', '0');
        row.setAttribute('aria-expanded', 'false');

        const toggle = (evt) => {
          if (step.el.getAttribute('aria-disabled') === 'true') return;
          if (evt.target.closest('.size-chart-link, modal-opener, a')) return; // let controls act
          evt.preventDefault();
          const willOpen = !step.el.classList.contains('is-open');
          this.openStep(willOpen ? step.el : null);
        };
        row.addEventListener('click', toggle);
        row.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter' || evt.key === ' ') toggle(evt);
        });
      });
    }

    /** Opens one step (or none) and collapses the rest. */
    openStep(el) {
      this.steps.forEach((step) => {
        const on = step.el === el;
        step.el.classList.toggle('is-open', on);
        const header = step.el.querySelector('.guided-step__header');
        if (header) header.setAttribute('aria-expanded', String(on));
      });
    }

    /** Opens the first unanswered (unlocked) step; collapses everything once all are answered.
     * @param {?Array} read - Precomputed readStep() results (refresh() already has them); recomputes
     * when omitted, for the one call site with no adjacent refresh() (the submit guard). */
    advanceAccordion(read) {
      const steps = read || this.steps.map((step) => this.readStep(step));
      const idx = steps.findIndex((r) => !r.satisfied);
      this.openStep(idx === -1 ? null : this.steps[idx].el);
    }

    // --- Subscription / frequency card ----------------------------------------------------------

    /** Wires the purchase-type toggle and plan tiles, and syncs their selected state. */
    setupFrequencyCard() {
      if (!this.freqCard) return;
      this.freqCard.querySelectorAll('[data-freq-type]').forEach((r) => {
        r.addEventListener('change', () => { this.syncFrequencyCard(); this.refresh(); });
      });
      this.freqCard.querySelectorAll('input[name="selling_plan"]').forEach((r) => {
        r.addEventListener('change', () => {
          // Choosing a delivery frequency implies a subscription.
          const sub = this.freqCard.querySelector('[data-freq-type][value="subscription"]');
          if (sub && !sub.checked) sub.checked = true;
          this.syncFrequencyCard();
          this.refresh();
        });
      });
      this.syncFrequencyCard();
    }

    /** Reflects purchase type on the option cards, and keeps a plan (un)selected to match. */
    syncFrequencyCard() {
      if (!this.freqCard) return;
      const typeInput = this.freqCard.querySelector('[data-freq-type]:checked');
      const purchase = typeInput ? typeInput.value : 'subscription';

      this.freqCard.querySelectorAll('[data-freq-option]').forEach((opt) => {
        opt.classList.toggle('is-selected', opt.dataset.freqOption === purchase);
      });

      const plan = this.freqCard.querySelector('input[name="selling_plan"]:checked');
      const value = this.freqCard.querySelector('.js-guided-frequency-text');
      if (value) value.textContent = plan ? (plan.dataset.planName || '') : '';
    }

    // --- Height / Pack selection ------------------------------------------------------------

    getSelectedHeight() {
      const btn = this.querySelector('.guided-height.is-selected');
      return btn ? btn.dataset.guidedHeight : null;
    }

    getSelectedPack() {
      const btn = this.querySelector('.guided-pack.is-selected:not([disabled])');
      return btn ? btn.dataset.guidedPack : null;
    }

    /** Marks one height button selected (clearing the rest) and refreshes pack availability. */
    selectHeightButton(btn) {
      this.querySelectorAll('.guided-height').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', String(on));
      });
      this.updatePackAvailability(btn.dataset.guidedHeight);
      // Height/pack are custom steps, not <variant-picker> options, so they don't get
      // VariantPicker.updateLabelText()'s "Select Color: White" treatment for free — mirror it here
      // so the collapsed accordion header shows what was actually chosen (matches the value shown
      // for Color/Size once picked, instead of just the static "Select height").
      const titleEl = btn.querySelector('.guided-product__title');
      const valueEl = this.querySelector('.js-guided-height-text');
      if (valueEl) valueEl.textContent = titleEl ? titleEl.textContent : btn.dataset.guidedHeight;
    }

    selectPackButton(btn) {
      this.querySelectorAll('.guided-pack').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', String(on));
      });
      const numEl = btn.querySelector('.opt-label--tile__num');
      const unitEl = btn.querySelector('.opt-label--tile__unit');
      const valueEl = this.querySelector('.js-guided-pack-text');
      if (valueEl) {
        valueEl.textContent = numEl
          ? `${numEl.textContent} ${unitEl ? unitEl.textContent : ''}`.trim()
          : btn.dataset.guidedPack;
      }
    }

    /** Enables only the pack sizes that exist for the chosen height; clears an invalid selection.
     *  Quiz tiles also show the lowest available 1-month subscription price for that height + pack. */
    updatePackAvailability(height) {
      const avail = height && this.map[height] ? this.map[height] : {};
      const showStartingPrice = ['subscription2-0-quiz', 'subscription-2-0'].includes(this.dataset.guidedView);

      this.querySelectorAll('.guided-pack').forEach((btn) => {
        const pack = btn.dataset.guidedPack;
        const ok = !!avail[pack];
        btn.disabled = !ok;
        btn.classList.toggle('is-unavailable', !ok);

        const priceEl = btn.querySelector('.guided-pack__price');
        if (priceEl && showStartingPrice) {
          const familyProduct = ok
            ? this.family.products.find((pr) => pr.height === height && pr.pack === pack)
            : null;
          const startingPrice = familyProduct && familyProduct.oneMonthPrice
            ? familyProduct.oneMonthPrice
            : '';

          priceEl.textContent = startingPrice ? `Starting at ${startingPrice}` : '';
          priceEl.hidden = !startingPrice;
        }

        if (!ok && btn.classList.contains('is-selected')) {
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    }

    /**
     * Resolves the product for (height, pack). Falls back through: the currently chosen pack,
     * then this product's own pack, then the first pack available for that height.
     * @returns {?string} product URL, or null.
     */
    resolveUrl(height, preferredPack) {
      const packMap = this.map[height];
      if (!packMap) return null;
      const pack = (preferredPack && packMap[preferredPack]) ? preferredPack
        : (this.family.current.pack && packMap[this.family.current.pack]) ? this.family.current.pack
          : Object.keys(packMap)[0];
      return packMap[pack] || null;
    }

    isCurrentUrl(url) {
      try {
        const target = new URL(url, window.location.origin).pathname;
        const current = new URL(this.getAttribute('data-product-url') || window.location.pathname, window.location.origin).pathname;
        return target === current;
      } catch (err) {
        return false;
      }
    }

    handleHeightClick(evt) {
      const btn = evt.currentTarget;
      this.userInteracted = true;
      this.hideAddedMessage();
      if (btn.classList.contains('is-selected')) return;

      this.selectHeightButton(btn);
      const height = btn.dataset.guidedHeight;
      // Load the product so COLOR/SIZE can render. Keep the customer's pack if they already chose
      // one and it exists for this height; otherwise default the pack underneath but leave the
      // PACK step unchosen (getSelectedPack() is null, so it isn't carried across the swap).
      const url = this.resolveUrl(height, this.getSelectedPack());
      if (url && !this.isCurrentUrl(url)) this.swapToUrl(url);
      else { this.advanceAccordion(this.refresh()); }
    }

    handlePackClick(evt) {
      const btn = evt.currentTarget;
      this.userInteracted = true;
      this.hideAddedMessage();
      if (btn.disabled || btn.classList.contains('is-selected')) return;

      const height = this.getSelectedHeight();
      if (!height) return;
      this.selectPackButton(btn);
      const url = (this.map[height] || {})[btn.dataset.guidedPack];
      if (url && !this.isCurrentUrl(url)) this.swapToUrl(url);
      else { this.advanceAccordion(this.refresh()); }
    }

    /** Warms the browser's HTTP cache for a likely HEIGHT swap target, so the click's own fetch()
     * in swapToUrl() (probably) resolves from cache instead of the network. */
    handleHeightHover(evt) {
      const btn = evt.currentTarget;
      if (btn.classList.contains('is-selected')) return;
      const url = this.resolveUrl(btn.dataset.guidedHeight, this.getSelectedPack());
      if (!url || this.isCurrentUrl(url)) return;
      const fetchUrl = new URL(url, window.location.origin);
      const carry = this.getCarryOverParams();
      Object.keys(carry).forEach((k) => fetchUrl.searchParams.set(k, carry[k]));
      fetch(fetchUrl.href).catch(() => {});
    }

    /** The choices to carry to another product: option values keyed by option name, plus dims + plan. */
    getCarryOverParams() {
      const params = {};
      this.steps.forEach((step) => {
        const read = this.readStep(step);
        if (step.kind === 'option' && read.value) params[read.key] = read.value;
      });
      const height = this.getSelectedHeight();
      if (height) params.guided_height = height;
      const pack = this.getSelectedPack();
      if (pack) params.guided_pack = pack;
      const plan = this.querySelector('input[name="selling_plan"]:checked');
      if (plan) params.selling_plan = plan.value;
      // None of the family products have this template assigned as their default yet, so ?view=
      // is what turns the guided flow on at all. Without carrying it, the swap's section fetch
      // (and the URL it lands on) resolves against the product's default template instead —
      // the plain, non-guided form comes back and looks like the whole flow reset. Read the
      // template suffix directly (data-guided-view, set from Liquid's `template.suffix`) rather
      // than the entry URL's own ?view= — that param is only present when a family member's own
      // product page was opened with ?view= appended. A page-template instance (see the optional
      // `product` picker on sections/main-product.liquid) IS this suffix natively and never has
      // ?view= in its URL at all, so it still needs a value to carry forward on the first swap.
      const view = this.getAttribute('data-guided-view') || new URLSearchParams(window.location.search).get('view');
      if (view) params.view = view;
      return params;
    }

    /**
     * Fetches the target product's page and swaps the whole <product-form> in place.
     *
     * This used to ask Shopify's Section Rendering API for just the "main" section (far less data
     * over the wire), but that API needs the section's full runtime id — `template--<numeric-id>
     * __main` — not the plain key from the JSON template. That numeric id is stable per TEMPLATE
     * FILE (every crew product shares one, every ankle product shares another, etc.) but there's no
     * way to know a *different* product's id from here without already having rendered its page —
     * so a hardcoded 'main' silently returned null for every single family member, 100% of the time,
     * and every swap was quietly falling back to a real navigation. Fetching the full page instead
     * has no such id to get wrong — it costs more bytes, but only the <product-form> is kept (see
     * fragment.querySelector('product-form') below); everything else in the fetched document,
     * scripts included, is discarded.
     * @param {string} href - The chosen product URL.
     */
    async swapToUrl(href) {
      const targetUrl = new URL(href, window.location.origin);
      const carry = this.getCarryOverParams();
      const productForm = this.closest('product-form');
      const bail = (reason, extra) => {
        // Merge carry in (same as the success path's fetchUrl/historyUrl) — carry.view is currently
        // the ONLY thing that keeps the guided template active on a sibling product's own page, so a
        // bare targetUrl here would silently drop the customer onto that product's plain, non-guided
        // PDP and lose their in-progress selections.
        const bailUrl = new URL(targetUrl.href);
        Object.keys(carry).forEach((k) => bailUrl.searchParams.set(k, carry[k]));
        console.warn(`guided-selector: swap fell back to a real navigation (${reason})`, { href: bailUrl.href, extra });
        window.location.href = bailUrl.href;
      };
      if (!productForm) { bail('no ancestor <product-form>'); return; }

      productForm.classList.add('is-guided-swapping');

      const fetchUrl = new URL(targetUrl.href);
      Object.keys(carry).forEach((k) => fetchUrl.searchParams.set(k, carry[k]));

      let html;
      try {
        const response = await fetch(fetchUrl.href);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        html = await response.text();
      } catch (err) {
        bail('product page fetch failed', err);
        return;
      }
      if (!html) { bail('product page fetch resolved empty'); return; }

      // Update the URL BEFORE parsing the fetched HTML into a fragment. createContextualFragment()
      // uses the HTML parser, which synchronously constructs/upgrades any custom elements it finds —
      // including <variant-picker>, whose constructor calls applySearchParams() to restore carried
      // Color/Size from window.location.search RIGHT THEN. If the URL hasn't been updated yet, that
      // read sees the params from BEFORE this swap (or none at all on the first swap), so Color/Size
      // silently fail to restore on the new product — which shows up as the accordion snapping back
      // open on Color after a Height/Pack swap, even though the swap itself succeeded.
      //
      // On a real product page, follow the swap to that sibling product's own URL — refresh/back/
      // share all resolve correctly there, which is the point of using its real URL at all. On a
      // page-template instance (sections/main-product.liquid's optional `product` picker) the page
      // doesn't belong to any one product, so there's no product URL to navigate to — swapping HEIGHT/
      // PACK should just fetch that product's data and drop it into the form in place, keeping the
      // page's own URL. Only the carried params (selection state) get merged into whichever URL wins.
      const historyUrl = theme.isProductPage() ? targetUrl : new URL(window.location.href);
      Object.keys(carry).forEach((k) => historyUrl.searchParams.set(k, carry[k]));
      window.history.pushState({}, '', historyUrl.href);

      const fragment = document.createRange().createContextualFragment(html);
      // Strip executable scripts — every needed custom element is already defined on this page, and the
      // variant/family data lives in <script type="application/json"> which is not executed anyway.
      fragment.querySelectorAll('script:not([type="application/json"])').forEach((s) => s.remove());

      const newForm = fragment.querySelector('product-form');
      if (!newForm) { bail('fetched page had no <product-form>', html.slice(0, 300)); return; }

      const doSwap = () => productForm.replaceWith(newForm);
      if (document.startViewTransition) document.startViewTransition(doSwap);
      else doSwap();
      // The new <product-form> and its <guided-selector> upgrade + init themselves via connectedCallback.
    }

    /** After a swap, restore the chosen height + pack from the URL (silently — we're already here). */
    applyDimsFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const height = params.get('guided_height');
      if (height) {
        const btn = this.querySelector(`.guided-height[data-guided-height="${this.cssEscape(height)}"]`);
        if (btn) this.selectHeightButton(btn);
      }
      const pack = params.get('guided_pack');
      if (pack) {
        const btn = this.querySelector(`.guided-pack[data-guided-pack="${this.cssEscape(pack)}"]`);
        if (btn && !btn.disabled) this.selectPackButton(btn);
      }
    }

    /** After a swap, restore a previously chosen frequency from the URL. */
    applyFrequencyFromUrl() {
      const planId = new URLSearchParams(window.location.search).get('selling_plan');
      if (!planId) return;
      const input = this.querySelector(`input[name="selling_plan"][value="${planId}"]`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new CustomEvent('change', { bubbles: true }));
      }
    }

    cssEscape(value) {
      return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
    }

    escape(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }
  }

  customElements.define('guided-selector', GuidedSelector);
}
